import React, { ErrorInfo, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getColor } from '../assets/theme';
import { ChevronRight, ErrorOutlined } from '@mui/icons-material';
import { handleCloseTab } from '@context/app-context';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryUIProps extends FallbackProps {
  componentStack: string;
  onClose: () => void;
}

const extractFirstComponent = (stack: string) => {
  const lines = stack.split('\n');

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('at ')) {
      return trimmedLine;
    }
  }

  return stack.trim().split('\n')[0];
};

const ErrorBoundaryUI: React.FC<ErrorBoundaryUIProps> = ({
  error,
  resetErrorBoundary,
  componentStack,
  onClose,
}) => {
  const { white } = getColor();

  const isChunkError = error?.message?.includes(
    'Failed to fetch dynamically imported module',
  );

  const firstComponent = extractFirstComponent(componentStack);

  return (
    <Dialog
      open
      sx={{ minWidth: '300px', '.MuiPaper-root': { overflowY: 'unset' } }}
      maxWidth='xl'
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-36px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999,
        }}
      >
        <Stack
          sx={{
            width: '79px',
            height: '79px',
            bgcolor: '#FFFF',
            borderRadius: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ErrorOutlined
            sx={{
              width: '59px',
              height: '59px',
              bgcolor: '#FFF8F7',
              p: '12px',
              borderRadius: '100%',
              color: isChunkError ? '#FFAB00' : '#FF5630',
            }}
          />
        </Stack>
      </Box>
      <Stack
        sx={{
          bgcolor: white,
          borderRadius: '4px',
          p: '36px 34px 24px 34px',
          justifyContent: 'flex-start',
          width: '460px',
        }}
      >
        <DialogTitle
          sx={{
            p: '16px 24px',
            fontWeight: 600,
            color: isChunkError ? 'warning.main' : 'error.main',
            fontSize: '20px',
            textAlign: 'center',
            wordWrap: 'break-word',
          }}
        >
          Unable to Proceed
        </DialogTitle>
        <DialogContent sx={{ p: '0 0 16px 0!important' }}>
          <Stack sx={{ pb: 2, gap: 1 }}>
            <Typography
              sx={{
                fontSize: '16px',
                wordWrap: 'break-word',
              }}
            >
              {isChunkError
                ? 'The system has been updated. Please reload the page to continue.'
                : 'An error occurred while processing this request :'}
            </Typography>
            {!isChunkError && (
              <>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: 'error.main',
                    wordWrap: 'break-word',
                  }}
                >
                  {`Error: ${error?.message}`}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: 'error.main',
                    wordWrap: 'break-word',
                  }}
                >
                  {firstComponent && `Component: ${firstComponent}`}
                </Typography>
              </>
            )}
          </Stack>
          <Typography
            sx={{
              pb: 2,
              fontSize: '14px',
              textAlign: 'left',
              color: '#637381',
              wordWrap: 'break-word',
            }}
          >
            If the {isChunkError ? 'warning' : 'error'} persists, submit a
            support ticket with a screenshot of your concern
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '0 0 8px 0' }}>
          <Stack direction='row' gap='8px'>
            {!isChunkError && (
              <Button
                onClick={() => {
                  resetErrorBoundary();
                  onClose();
                }}
              >
                Close
              </Button>
            )}
            <Link
              to='https://servoitsolutions.ph/support'
              target='_blank'
              style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
              onClick={() => {
                resetErrorBoundary();
                onClose();
              }}
            >
              <Button
                endIcon={<ChevronRight />}
                variant={isChunkError ? 'text' : 'contained'}
              >
                Servo IT Solutions - Support
              </Button>
            </Link>
            {isChunkError && (
              <Button
                onClick={() => {
                  window.removeEventListener('beforeunload', handleCloseTab);
                  window.location.reload();
                }}
                variant='contained'
                sx={{ whiteSpace: 'nowrap' }}
              >
                Reload
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

const ErrorBoundaryWrapper = (props: ErrorBoundaryProps) => {
  const [info, setInfo] = useState('');

  const handleError = (error: Error, info: ErrorInfo) => {
    const componentStack = info.componentStack || '';
    console.log('WEB ERROR:', error);
    console.log('WEB ERROR INFO:', info);
    setInfo(componentStack);
  };

  return (
    <ErrorBoundary
      FallbackComponent={(data) => (
        <ErrorBoundaryUI
          error={data.error}
          resetErrorBoundary={data.resetErrorBoundary}
          componentStack={info}
          onClose={() => setInfo('')}
        />
      )}
      onError={handleError}
    >
      {props.children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryWrapper;
